// ---------------------------------------------------------------------------
// Sprint C+++ Plus (Feb 2026) – Vollständige Prüfakte-Anzeige
// ---------------------------------------------------------------------------
// Rendert ausschließlich aus snap.blocks (eingefroren beim Release durch
// build_world_snapshot() / deepcopy). Keine Live-Daten der aktuellen Welt.
// Dies ist die BILDSCHIRM-Ansicht. Das prüfsichere PDF wird serverseitig
// (WeasyPrint) erzeugt und über "PDF herunterladen" geladen – kein Browser-Druck.
// ---------------------------------------------------------------------------

import { Check } from "lucide-react";

export const BLOCK_LABEL = {
  risk_assessment: "Gefährdungsbeurteilung (GBU)",
  operating_instruction: "Betriebsanweisung",
  instruction: "Unterweisung",
  questions: "Verständnisfragen",
  maintenance_hints: "Prüf- / Wartungshinweise",
  recurrence: "Wiedervorlage-Intervall",
};

export const BLOCK_ORDER = [
  "risk_assessment",
  "operating_instruction",
  "instruction",
  "questions",
  "maintenance_hints",
  "recurrence",
];

const RECURRENCE_LABEL = {
  einmalig: "einmalig",
  monatlich: "monatlich",
  vierteljaehrlich: "vierteljährlich",
  halbjaehrlich: "halbjährlich",
  jaehrlich: "jährlich",
  zwei_jahre: "alle 2 Jahre",
};

export function recurrenceLabel(value) {
  if (!value) return "–";
  return RECURRENCE_LABEL[value] || value;
}

const SECTION_CLASS =
  "mt-10 pt-8 border-t border-slate-300 break-inside-avoid print:break-before-page print:mt-0 print:pt-6";
const SUB_HEADING_CLASS =
  "text-xs uppercase tracking-widest text-slate-500 mt-4 mb-1";
const BODY_CLASS =
  "text-sm text-slate-800 leading-relaxed whitespace-pre-wrap";

function hasRecurrenceData(rec, snap) {
  if (!rec) return false;
  return Boolean(
    rec.recurrence ||
      snap?.recurrence ||
      rec.repeat_interval_months != null ||
      rec.gbu_recurrence ||
      rec.wartung_recurrence ||
      rec.ai_recurrence_reason
  );
}

export function WeltfreigabeContent({ snap }) {
  const blocks = snap?.blocks;
  if (!blocks) {
    // Snapshot stammt aus einer alten Version vor Sprint C+++ Plus. Migration
    // sollte das Feld inzwischen befüllt haben — wenn nicht, zeigen wir
    // einen klaren Hinweis statt leiser Lücke.
    return (
      <div
        className="mt-8 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-4"
        data-testid="weltfreigabe-content-missing"
      >
        Für diesen Nachweis liegen die vollständigen Bausteine-Inhalte noch
        nicht im Snapshot vor. Beim nächsten Backend-Start werden die Inhalte
        über die Migration nachgezogen.
      </div>
    );
  }

  const risk = blocks.risk_assessment;
  const op = blocks.operating_instruction;
  const instruction = blocks.instruction || [];
  const questions = blocks.questions || [];
  const maint = blocks.maintenance_hints;
  const rec = blocks.recurrence_details || {};
  const showRecurrence = hasRecurrenceData(rec, snap);

  return (
    <div data-testid="weltfreigabe-content">
      {risk && <RiskAssessmentSection risk={risk} />}
      {op && <OperatingInstructionSection op={op} />}
      {instruction.length > 0 && (
        <InstructionSection blocks={instruction} />
      )}
      {questions.length > 0 && <QuestionsSection questions={questions} />}
      {maint && <MaintenanceSection maint={maint} />}
      {showRecurrence && <RecurrenceSection rec={rec} snap={snap} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1) Gefährdungsbeurteilung
// ---------------------------------------------------------------------------

function RiskAssessmentSection({ risk }) {
  return (
    <section
      className={SECTION_CLASS}
      data-testid="weltfreigabe-content-risk-assessment"
    >
      <SectionHeading>{BLOCK_LABEL.risk_assessment}</SectionHeading>
      {risk.responsible_role && (
        <p className="text-xs text-slate-500 mt-3">
          Verantwortlich:{" "}
          <span className="text-slate-800 font-medium">
            {risk.responsible_role}
          </span>
        </p>
      )}
      {Array.isArray(risk.hazards) && risk.hazards.length > 0 ? (
        <ol className="mt-4 space-y-5">
          {risk.hazards.map((h, idx) => (
            <li
              key={idx}
              className="border-l-2 border-slate-300 pl-4 break-inside-avoid"
              data-testid={`weltfreigabe-hazard-${idx}`}
            >
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-xs uppercase tracking-widest text-slate-500">
                  Gefahr {idx + 1}
                </span>
                {h.category && (
                  <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                    {h.category}
                  </span>
                )}
                {h.residual_risk && (
                  <span className="text-xs text-slate-600">
                    Restrisiko:{" "}
                    <span className="font-medium">{h.residual_risk}</span>
                  </span>
                )}
              </div>
              {h.description && (
                <p className="text-slate-900 font-medium mt-1">
                  {h.description}
                </p>
              )}
              <MeasureBlock label="Technische Maßnahmen" value={h.measures_technical} />
              <MeasureBlock label="Organisatorische Maßnahmen" value={h.measures_organizational} />
              <MeasureBlock label="Persönliche Schutzmaßnahmen" value={h.measures_personal} />
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-sm text-slate-500 mt-3 italic">
          Keine Einzel-Gefährdungen im Snapshot dokumentiert.
        </p>
      )}
    </section>
  );
}

function MeasureBlock({ label, value }) {
  if (!value) return null;
  return (
    <>
      <div className={SUB_HEADING_CLASS}>{label}</div>
      <p className={BODY_CLASS}>{value}</p>
    </>
  );
}

// ---------------------------------------------------------------------------
// 2) Betriebsanweisung
// ---------------------------------------------------------------------------

function OperatingInstructionSection({ op }) {
  return (
    <section
      className={SECTION_CLASS}
      data-testid="weltfreigabe-content-operating-instruction"
    >
      <SectionHeading>{BLOCK_LABEL.operating_instruction}</SectionHeading>
      <FieldText label="Anwendungsbereich" value={op.purpose} />
      <ListField label="Hauptgefahren" items={op.main_hazards} />
      <ListField label="Schutzmaßnahmen" items={op.protective_measures} />
      <FieldText label="Verhalten im Notfall" value={op.behavior_in_emergency} />
      <FieldText label="Erste Hilfe" value={op.first_aid} />
      <FieldText label="Entsorgung / Lagerung" value={op.disposal_or_storage} />
    </section>
  );
}

function FieldText({ label, value }) {
  if (!value) return null;
  return (
    <>
      <div className={SUB_HEADING_CLASS}>{label}</div>
      <p className={BODY_CLASS}>{value}</p>
    </>
  );
}

function ListField({ label, items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <>
      <div className={SUB_HEADING_CLASS}>{label}</div>
      <ul className="list-disc list-outside pl-5 space-y-1 text-sm text-slate-800">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </>
  );
}

// ---------------------------------------------------------------------------
// 3) Unterweisung
// ---------------------------------------------------------------------------

function InstructionSection({ blocks }) {
  return (
    <section
      className={SECTION_CLASS}
      data-testid="weltfreigabe-content-instruction"
    >
      <SectionHeading>{BLOCK_LABEL.instruction}</SectionHeading>
      <div className="mt-4 space-y-5">
        {blocks.map((block, idx) => (
          <div
            key={idx}
            className="break-inside-avoid"
            data-testid={`weltfreigabe-instruction-${idx}`}
          >
            {block.heading && (
              <h3 className="font-heading text-base font-semibold text-slate-900">
                {idx + 1}. {block.heading}
              </h3>
            )}
            {block.body && (
              <p className={`${BODY_CLASS} mt-1`}>{block.body}</p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 4) Verständnisfragen
// ---------------------------------------------------------------------------

function QuestionsSection({ questions }) {
  return (
    <section
      className={SECTION_CLASS}
      data-testid="weltfreigabe-content-questions"
    >
      <SectionHeading>{BLOCK_LABEL.questions}</SectionHeading>
      <ol className="mt-4 space-y-5">
        {questions.map((q, idx) => (
          <li
            key={q.id || idx}
            className="break-inside-avoid"
            data-testid={`weltfreigabe-question-${idx}`}
          >
            <p className="text-slate-900 font-medium">
              {idx + 1}. {q.text}
            </p>
            {Array.isArray(q.options) && (
              <ul className="mt-2 space-y-1.5">
                {q.options.map((opt, oIdx) => {
                  const isCorrect = q.correct_index === oIdx;
                  return (
                    <li
                      key={oIdx}
                      className={`flex items-start gap-2 text-sm ${
                        isCorrect ? "text-emerald-900" : "text-slate-700"
                      }`}
                    >
                      <span
                        className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                          isCorrect
                            ? "border-emerald-600 bg-emerald-600 text-white"
                            : "border-slate-300"
                        }`}
                      >
                        {isCorrect && <Check className="w-2.5 h-2.5" />}
                      </span>
                      <span>
                        {opt}
                        {isCorrect && (
                          <span className="text-xs text-emerald-700 ml-2 font-medium">
                            (korrekt)
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 5) Prüf- / Wartungshinweise
// ---------------------------------------------------------------------------

function MaintenanceSection({ maint }) {
  return (
    <section
      className={SECTION_CLASS}
      data-testid="weltfreigabe-content-maintenance"
    >
      <SectionHeading>{BLOCK_LABEL.maintenance_hints}</SectionHeading>
      {Array.isArray(maint.inspection_items) &&
        maint.inspection_items.length > 0 && (
          <ul className="mt-4 space-y-3">
            {maint.inspection_items.map((item, idx) => (
              <li
                key={idx}
                className="border-l-2 border-slate-300 pl-4 break-inside-avoid"
                data-testid={`weltfreigabe-inspection-${idx}`}
              >
                <p className="text-sm text-slate-900">{item.description}</p>
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1 text-xs text-slate-600">
                  {item.interval && (
                    <span>
                      Intervall:{" "}
                      <span className="font-medium text-slate-800">
                        {item.interval}
                      </span>
                    </span>
                  )}
                  {item.who && (
                    <span>
                      Durch:{" "}
                      <span className="font-medium text-slate-800">
                        {item.who}
                      </span>
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      {maint.next_check_recommended_in && (
        <p className="mt-4 text-sm text-slate-700">
          <span className="text-slate-500">Nächste Prüfung empfohlen in: </span>
          <span className="font-medium">
            {maint.next_check_recommended_in}
          </span>
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// 6) Wiedervorlage
// ---------------------------------------------------------------------------

function RecurrenceSection({ rec, snap }) {
  return (
    <section
      className={SECTION_CLASS}
      data-testid="weltfreigabe-content-recurrence"
    >
      <SectionHeading>Wiedervorlage</SectionHeading>
      <div className="mt-4 grid sm:grid-cols-2 gap-y-3 gap-x-10">
        <div>
          <div className={SUB_HEADING_CLASS}>Unterweisungs-Intervall</div>
          <div className="text-slate-900 font-medium">
            {recurrenceLabel(rec.recurrence || snap?.recurrence)}
          </div>
        </div>
        {rec.repeat_interval_months != null && (
          <div>
            <div className={SUB_HEADING_CLASS}>Wiederholung alle</div>
            <div className="text-slate-900 font-medium">
              {rec.repeat_interval_months} Monate
            </div>
          </div>
        )}
        {rec.gbu_recurrence && (
          <div>
            <div className={SUB_HEADING_CLASS}>GBU-Review</div>
            <div className="text-slate-900 font-medium">
              {recurrenceLabel(rec.gbu_recurrence)}
            </div>
          </div>
        )}
        {rec.wartung_recurrence && (
          <div>
            <div className={SUB_HEADING_CLASS}>Wartungs-Zyklus</div>
            <div className="text-slate-900 font-medium">
              {recurrenceLabel(rec.wartung_recurrence)}
            </div>
          </div>
        )}
      </div>
      {rec.ai_recurrence_reason && (
        <p className="mt-4 text-xs text-slate-500 italic">
          Begründung: {rec.ai_recurrence_reason}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

function SectionHeading({ children }) {
  return (
    <h2 className="font-heading text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
      {children}
    </h2>
  );
}
